import Business from "@mui/icons-material/Business";
import Description from "@mui/icons-material/Description";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { fetchBrands } from "store/brandStore";
import { useAppDispatch, useAppSelector } from "store/store";

export type PerimeterDisplayType = "badge" | "checkbox";

export interface PerimeterOption {
    id: string;
    label: string;
}

/**
 * Configuration for a permission subject that should be displayed as a perimeter
 */
export interface PerimeterConfig {
    subject: string; // The permission subject (e.g., "CompanyBrand", "ContractType")
    action: string; // The action to use (e.g., "manage", "read")
    field: string; // The field to apply condition on (e.g., "id", "status")
    operator: string; // The condition operator (e.g., "$in", "$lte")
    displayType: PerimeterDisplayType; // How to display the options (badge or checkbox)
    icon: ReactNode;
    labelKey: string; // Translation key for section label
    descriptionKey: string; // Translation key for section description
    availableOptions: PerimeterOption[]; // Available values for this perimeter
}

export interface PerimeterOptionsData {
    perimeters: PerimeterConfig[];
    isPending: boolean;
}

/**
 * Hook that returns perimeter configuration.
 * Perimeters are special permissions that define scope/boundaries for a role.
 * They map to permission subjects but are displayed separately for UX purposes.
 *
 * @returns Configuration for perimeter subjects and their available options
 */
export const usePerimeterOptions = (): PerimeterOptionsData => {
    const dispatch = useAppDispatch();
    const { brands } = useAppSelector((state) => state.brands);
    const [isPending, setIsPending] = useState(true);

    const filteredBrands = brands.filter((brand) => brand.id !== -1);

    // Fetch brands on mount
    useEffect(() => {
        setIsPending(true);
        dispatch(fetchBrands(false)).finally(() => {
            setIsPending(false);
        });
    }, [dispatch]);

    const perimeters: PerimeterConfig[] = [];

    // Only add CompanyBrand perimeter if brands are available
    if (filteredBrands && filteredBrands.length > 0) {
        perimeters.push({
            subject: "CompanyBrand",
            action: "manage",
            field: "id",
            operator: "$in",
            displayType: "badge",
            icon: <Business />,
            labelKey: "Roles.Modal.AuthorizedBrands",
            descriptionKey: "Roles.Modal.AuthorizedBrandsDescription",
            availableOptions: filteredBrands.map((brand) => ({
                id: String(brand.id),
                label: brand.name,
            })),
        });
    }

    // perimeters.push({
    //     subject: "Beneficiary",
    //     action: "manage",
    //     field: "activityType",
    //     operator: "$in",
    //     displayType: "checkbox",
    //     icon: <Description />,
    //     labelKey: "Roles.Modal.ContractTypes",
    //     descriptionKey: "Roles.Modal.ContractTypesDescription",
    //     availableOptions: [
    //         { id: "type1", label: "Authors" },
    //         { id: "type2", label: "Translators" },
    //         { id: "type3", label: "Others" },
    //     ],
    // });

    return {
        perimeters,
        isPending,
    };
};
